
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Heart } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const NewsletterSignup = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const { toast } = useToast();

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    
    setIsSubscribing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-newsletter-confirmation', {
        body: { email: newsletterEmail }
      });

      if (error) {
        throw error;
      }

      console.log('Newsletter subscription response:', data);
      
      // Handle success response with better messaging
      if (data.isExisting) {
        toast({
          title: "Already Subscribed! 📧",
          description: "You're already part of our newsletter family! Check your email for confirmation.",
        });
      } else {
        toast({
          title: "Successfully Subscribed! 🎉",
          description: "Welcome to the Bobo & Teedee® family! Check your email for a confirmation message.",
        });
      }
      
      setNewsletterEmail('');
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      
      // Handle different types of errors
      let errorMessage = "There was an error subscribing to our newsletter. Please try again.";
      
      if (error.message) {
        // If we have a specific error message from the server, use it
        errorMessage = error.message;
      }
      
      toast({
        title: "Subscription Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <Card className="shadow-xl bg-white/80 backdrop-blur-sm border-2 border-bobo-green/20">
      <CardHeader>
        <CardTitle className="text-2xl text-bobo-green flex items-center">
          <Heart className="mr-3 h-6 w-6" />
          Stay Connected!
        </CardTitle>
        <p className="text-gray-600 mt-2">
          Subscribe to our newsletter for new episodes, activities, and special announcements!
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleNewsletterSubmit} className="space-y-4">
          <div>
            <Label htmlFor="newsletter-email" className="text-bobo-green font-semibold">Email Address</Label>
            <Input
              id="newsletter-email"
              type="email"
              required
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              className="mt-1 border-sky-blue/30 focus:border-bobo-green"
              placeholder="your.email@example.com"
            />
          </div>
          <Button 
            type="submit"
            disabled={isSubscribing}
            className="w-full bg-bobo-green hover:bg-bobo-green/90 text-white text-lg py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            {isSubscribing ? "Subscribing..." : "Subscribe to Newsletter"}
          </Button>
          <p className="text-xs text-gray-500 text-center">
            We respect your privacy and will never spam you. Unsubscribe anytime!
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default NewsletterSignup;
